/**
 * The private workbench — what used to be the entire app.
 *
 * Same state-machine view selection the old App.tsx had, just lifted
 * here so the public storefront can live above it at the route level.
 *
 * Lives at /workbench/*. A small "Storefront" link in the corner lets
 * the user pop out to the public landing page if they want to.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { useAuth } from '@/auth/useAuth';
import { useLang } from '@/i18n/useLang';
import { AccountMenu } from '@/components/AccountMenu';
import { BalaPage } from './BalaPage';
import { BnnReportPage } from './BnnReportPage';
import { BusinessPartnerCompatPage } from './BusinessPartnerCompatPage';
import { CareerCompatPage } from './CareerCompatPage';
import { CareerDashboard } from './CareerDashboard';
import { CompatibilityPage } from './CompatibilityPage';
import { CompleteProfilePage } from './CompleteProfilePage';
import { HealthPage } from './HealthPage';
import { JanmaKundliPage } from './JanmaKundliPage';
import { KarmicPathPage } from './KarmicPathPage';
import { MenuPage } from './MenuPage';
import type { ReportKey } from './MenuPage';
import { PitraDoshaPage } from './PitraDoshaPage';
import { RectifyPage } from './RectifyPage';
import { SadeSatiPage } from './SadeSatiPage';
import {
  BusinessYogaPage,
  LegalYogaPage,
  ManglikPage,
  ProgenyYogaPage,
  WealthPage,
} from './specialty_pages';
import { VarshaphalPage } from './VarshaphalPage';
import { VidyaYogaPage } from './VidyaYogaPage';
import { VisaYogaPage } from './VisaYogaPage';

type View = 'menu' | ReportKey;

export function WorkbenchShell() {
  const { user } = useAuth();
  const { lang } = useLang();
  const [view, setView] = useState<View>('menu');

  const back = () => setView('menu');

  const legacyPages: View[] = [
    'career', 'bnn', 'karmic', 'health',
    'compatibility', 'varshaphal', 'vidya', 'bala', 'pitra-dosha',
    'janma-kundli',
  ];
  const showGlobalBack = legacyPages.includes(view);

  return (
    <div className="min-h-screen">
      <div className="mx-auto flex max-w-screen-2xl items-center justify-between px-4 pt-3 lg:px-6">
        {showGlobalBack ? (
          <button
            type="button"
            onClick={back}
            className="text-xs text-saffron-300 hover:underline"
          >
            ← {lang === 'hi' ? 'मेन्यू पर वापस' : 'Back to menu'}
          </button>
        ) : (
          <span />
        )}
        <div className="flex items-center gap-3 text-xs">
          {user?.role === 'professor' && (
            <Link
              to="/workbench/admin/orders"
              className="text-saffron-300 hover:underline"
            >
              {lang === 'hi' ? 'व्यवस्थापक' : 'Admin'}
            </Link>
          )}
          <Link to="/" className="text-stone-400 hover:text-saffron-200">
            {lang === 'hi' ? 'स्टोर खोलें' : 'Open storefront'} →
          </Link>
          <AccountMenu />
        </div>
      </div>

      {view === 'menu' && <MenuPage onSelect={(k) => setView(k)} />}
      {view === 'janma-kundli' && <JanmaKundliPage />}
      {view === 'career' && <CareerDashboard />}
      {view === 'bnn' && <BnnReportPage />}
      {view === 'karmic' && <KarmicPathPage />}
      {view === 'health' && <HealthPage />}
      {view === 'compatibility' && <CompatibilityPage />}
      {view === 'varshaphal' && <VarshaphalPage />}
      {view === 'vidya' && <VidyaYogaPage />}
      {view === 'bala' && <BalaPage />}
      {view === 'pitra-dosha' && <PitraDoshaPage />}
      {view === 'wealth' && <WealthPage onBack={back} />}
      {view === 'manglik' && <ManglikPage onBack={back} />}
      {view === 'visa' && <VisaYogaPage onBack={back} />}
      {view === 'business' && <BusinessYogaPage onBack={back} />}
      {view === 'progeny' && <ProgenyYogaPage onBack={back} />}
      {view === 'legal' && <LegalYogaPage onBack={back} />}
      {view === 'sade-sati' && <SadeSatiPage onBack={back} />}
      {view === 'rectify' && <RectifyPage onBack={back} />}
      {view === 'career-compat' && <CareerCompatPage onBack={back} />}
      {view === 'business-partner-compat' && <BusinessPartnerCompatPage onBack={back} />}
      {view === 'complete-profile' && <CompleteProfilePage onBack={back} />}
    </div>
  );
}
